/**
 * Main Audio Player orchestrator composable
 * Combines navigation, waveform, and native audio element management
 */

import { ref, computed, watch, onUnmounted, type Ref, type ComputedRef, shallowRef, nextTick } from 'vue'
import type {
  AudioPlayerConfig,
  AudioPlayerState,
  AudioPlayerCallbacks,
  AudioPlayerOptions,
  WaveformData,
} from '../types'
import type { AudioAssetListItem, AudioPlaybackInfo } from '../../types/asset'
import type { PlayerError, TimeRange } from '../../types/base'
import type { PlayState, PlaybackInfoState, CollectionItem } from '../../types/player'
import { useAudioNavigation, type UseAudioNavigationReturn } from './useAudioNavigation'
import { useAudioWaveform, type UseAudioWaveformReturn } from './useAudioWaveform'
import { calculateBufferedRanges, clamp, PLAYBACK_SPEEDS, type PlaybackSpeed } from '../utils/audio-utils'
import { playbackDownloadUrl } from '@/api/visitor/playback'
import { usePlayhead } from '../../common/composables/usePlayhead'

export interface UseAudioPlayerOptions extends AudioPlayerOptions {
  callbacks?: AudioPlayerCallbacks
  /**
   * Whether the listener may move the playhead — the publication's `seeking`, `true` when absent.
   * Read on every move, so a page that turns it on later is followed.
   */
  canSeek?: () => boolean
}

export interface UseAudioPlayerReturn {
  // Audio element ref
  audioRef: Ref<HTMLAudioElement | null>

  // Items
  items: Ref<CollectionItem<AudioAssetListItem>[]>
  totalItems: ComputedRef<number>

  // Current item
  currentIndex: Ref<number>
  currentItem: ComputedRef<CollectionItem<AudioAssetListItem> | undefined>
  currentAsset: ComputedRef<AudioAssetListItem | undefined>
  currentSourceUrl: ComputedRef<string | undefined>

  // Playback state
  playState: Ref<PlayState>
  isPlaying: ComputedRef<boolean>
  isPaused: ComputedRef<boolean>
  isBuffering: ComputedRef<boolean>
  isLoading: ComputedRef<boolean>

  // Time
  currentTime: Ref<number>
  duration: Ref<number>
  buffered: Ref<TimeRange[]>
  progress: ComputedRef<number>

  // Volume
  volume: Ref<number>
  isMuted: Ref<boolean>

  // Playback speed
  playbackSpeed: Ref<number>
  availableSpeeds: readonly PlaybackSpeed[]

  // Navigation
  canGoNext: ComputedRef<boolean>
  canGoPrev: ComputedRef<boolean>
  isLoop: Ref<boolean>
  isShuffle: Ref<boolean>
  isAutoPlayNext: Ref<boolean>
  goToIndex: (index: number) => void
  next: () => void
  prev: () => void
  first: () => void
  last: () => void
  toggleLoop: () => void
  toggleShuffle: () => void
  toggleAutoPlayNext: () => void

  // Waveform
  waveform: Ref<WaveformData | undefined>
  isWaveformLoading: Ref<boolean>

  // Fullscreen
  isFullscreen: Ref<boolean>
  toggleFullscreen: () => void

  // Playback controls
  play: () => Promise<void>
  pause: () => void
  togglePlay: () => Promise<void>
  /**
   * Whether the listener may move the playhead (`seeking`). While it is `false` the waveform and
   * the bar are drawn still, the keyboard's and the chapters' moves are refused, and a move the
   * element makes that the player did not ask for is put back (`common/composables/usePlayhead.ts`).
   */
  canSeek: ComputedRef<boolean>
  /** **The listener's move** of the playhead: refused while `canSeek` is `false`. */
  seek: (time: number) => void
  seekPercent: (percent: number) => void
  /** **A move the listener did not make** — the command of the page that framed the player. */
  seekTo: (time: number) => void
  setVolume: (volume: number) => void
  setMuted: (muted: boolean) => void
  toggleMute: () => void
  setPlaybackSpeed: (speed: number) => void

  // Playback info management
  currentPlaybackInfo: ComputedRef<AudioPlaybackInfo | undefined>
  getPlaybackInfo: (uid: string) => AudioPlaybackInfo | undefined
  setPlaybackInfo: (uid: string, info: AudioPlaybackInfo) => void
  requestPlaybackInfo: (uid: string) => void

  // Downloads
  hasDownloads: ComputedRef<boolean>
  sourceDownloadUrl: ComputedRef<string | undefined>

  // Error
  error: Ref<PlayerError | undefined>
  clearError: () => void

  // State
  state: ComputedRef<AudioPlayerState>

  // Lifecycle
  destroy: () => void
}

/**
 * Main Audio Player orchestrator
 */
export function useAudioPlayer(options: UseAudioPlayerOptions): UseAudioPlayerReturn {
  const { config, initialPlaybackInfo, coverArt, callbacks } = options

  // Audio element ref
  const audioRef = shallowRef<HTMLAudioElement | null>(null)

  // Every move of the playhead the player makes, and the net beneath the controls for the ones it
  // did not: put back while the listener may not seek.
  const playhead = usePlayhead({
    element: () => audioRef.value,
    canSeek: options.canSeek,
    span: () => ({ start: 0, end: audioRef.value?.duration ?? 0 }),
  })

  // Items state (CollectionItem wraps AudioAssetListItem)
  const items = ref<CollectionItem<AudioAssetListItem>[]>(config.items || [])

  // Playback state
  const playState = ref<PlayState>('idle')
  const currentTime = ref(0)
  const duration = ref(0)
  const buffered = ref<TimeRange[]>([])

  // Volume
  const volume = ref(1)
  const isMuted = ref(config.muted ?? false)

  // Start-at (consumed once on first load)
  const pendingStartAt = ref(config.startAt ?? 0)

  // Autoplay: schedule play when audio is ready (readyState >= HAVE_FUTURE_DATA)
  const pendingAutoPlay = ref(false)

  function scheduleAutoPlay(): void {
    const audio = audioRef.value
    if (audio && audio.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
      // Already ready — play immediately
      play()
    } else {
      pendingAutoPlay.value = true
    }
  }

  // Playback speed
  const playbackSpeed = ref(config.playbackSpeed ?? 1)

  // Fullscreen
  const isFullscreen = ref(false)

  // Error state
  const error = ref<PlayerError | undefined>()

  // Playback info cache
  const playbackInfoCache = ref<Map<string, AudioPlaybackInfo>>(
    initialPlaybackInfo ? new Map([[initialPlaybackInfo.uid, initialPlaybackInfo]]) : new Map()
  )

  // Extract assets from collection items for the navigation composable
  const assetItems = computed(() => items.value.map((item) => item.asset))

  // Initialize navigation composable
  const navigation: UseAudioNavigationReturn = useAudioNavigation({
    items: assetItems,
    initialIndex: config.currentIndex || 0,
    loop: config.loop ?? false,
    shuffle: config.shuffle ?? false,
    autoAdvance: config.autoPlayNext ?? true,
    onIndexChange: (index) => {
      const item = items.value[index]
      if (item) {
        callbacks?.onItemChange?.(index, item)
      }
      // Load playback info for new current item
      loadCurrentPlaybackInfo()
      // Load waveform for new item
      loadCurrentWaveform()
    },
    onTrackEnd: (_index) => {
      callbacks?.onEnded?.()
    },
    onCollectionEnd: () => {
      callbacks?.onCollectionEnd?.()
      playState.value = 'ended'
    },
  })

  // Initialize waveform composable
  const waveformComposable: UseAudioWaveformReturn = useAudioWaveform({
    onLoad: (waveform) => {
      callbacks?.onWaveformLoad?.(waveform)
    },
  })

  // Computed
  const totalItems = computed(() => items.value.length)

  const currentItem = computed(() => items.value[navigation.currentIndex.value])

  const currentAsset = computed(() => currentItem.value?.asset)

  const currentPlaybackInfo = computed(() => {
    const asset = currentAsset.value
    if (!asset) return undefined
    return playbackInfoCache.value.get(asset.uid)
  })

  const currentSourceUrl = computed(() => {
    return currentPlaybackInfo.value?.source?.url
  })

  const hasDownloads = computed(() => {
    const info = currentPlaybackInfo.value
    const hasLinkedFiles = !!(info?.linkedFiles && info.linkedFiles.length > 0)
    const hasSourceDownload = !!config.allowSourceDownload
    return hasLinkedFiles || hasSourceDownload
  })

  const sourceDownloadUrl = computed(() => {
    if (!config.allowSourceDownload || !config.playbackToken) return undefined
    const asset = currentAsset.value
    if (!asset) return undefined
    return playbackDownloadUrl(asset.uid, config.playbackToken)
  })

  const isPlaying = computed(() => playState.value === 'playing')
  const isPaused = computed(() => playState.value === 'paused')
  const isBuffering = computed(() => playState.value === 'buffering')
  const isLoading = computed(() => playState.value === 'loading')

  const progress = computed(() => {
    if (duration.value <= 0) return 0
    return (currentTime.value / duration.value) * 100
  })

  // Playback info state for external access
  const playbackInfoState = computed<PlaybackInfoState>(() => ({
    isLoading: playState.value === 'loading' && !currentPlaybackInfo.value,
    isLoaded: !!currentPlaybackInfo.value,
  }))

  // Full state
  const state = computed<AudioPlayerState>(() => ({
    playState: playState.value,
    currentTime: currentTime.value,
    duration: duration.value,
    volume: volume.value,
    isMuted: isMuted.value,
    playbackSpeed: playbackSpeed.value,
    isNormalizeVolume: config.normalizeVolume ?? false,
    waveform: waveformComposable.waveformData.value,
    isWaveformLoading: waveformComposable.isLoading.value,
    isCrossfading: false,
    crossfadeProgress: 0,
    currentChapterIndex: -1,
    error: error.value,
    playbackInfo: playbackInfoState.value,
  }))

  /**
   * Binds event listeners to the audio element
   */
  function bindAudioEvents(audio: HTMLAudioElement): void {
    audio.addEventListener('loadstart', handleLoadStart)
    audio.addEventListener('loadedmetadata', handleLoadedMetadata)
    audio.addEventListener('loadeddata', handleLoadedData)
    audio.addEventListener('canplay', handleCanPlay)
    audio.addEventListener('play', handlePlay)
    audio.addEventListener('pause', handlePause)
    audio.addEventListener('playing', handlePlaying)
    audio.addEventListener('waiting', handleWaiting)
    audio.addEventListener('timeupdate', handleTimeUpdate)
    audio.addEventListener('durationchange', handleDurationChange)
    audio.addEventListener('progress', handleProgress)
    audio.addEventListener('seeking', playhead.onSeeking)
    audio.addEventListener('seeked', handleSeeked)
    audio.addEventListener('ended', handleEnded)
    audio.addEventListener('error', handleError)
    audio.addEventListener('volumechange', handleVolumeChange)
    audio.addEventListener('ratechange', handleRateChange)
  }

  /**
   * Unbinds event listeners from the audio element
   */
  function unbindAudioEvents(audio: HTMLAudioElement): void {
    audio.removeEventListener('loadstart', handleLoadStart)
    audio.removeEventListener('loadedmetadata', handleLoadedMetadata)
    audio.removeEventListener('loadeddata', handleLoadedData)
    audio.removeEventListener('canplay', handleCanPlay)
    audio.removeEventListener('play', handlePlay)
    audio.removeEventListener('pause', handlePause)
    audio.removeEventListener('playing', handlePlaying)
    audio.removeEventListener('waiting', handleWaiting)
    audio.removeEventListener('timeupdate', handleTimeUpdate)
    audio.removeEventListener('durationchange', handleDurationChange)
    audio.removeEventListener('progress', handleProgress)
    audio.removeEventListener('seeking', playhead.onSeeking)
    audio.removeEventListener('seeked', handleSeeked)
    audio.removeEventListener('ended', handleEnded)
    audio.removeEventListener('error', handleError)
    audio.removeEventListener('volumechange', handleVolumeChange)
    audio.removeEventListener('ratechange', handleRateChange)
  }

  // Audio event handlers
  function handleLoadStart(): void {
    playState.value = 'loading'
    playhead.onLoadStart()
    // Whatever was buffered belonged to the track being replaced. Left alone it stays on the bar
    // until the first `progress` of the new one.
    buffered.value = []
  }

  function handleLoadedData(): void {
    consumeAutoPlay()
  }

  function handleLoadedMetadata(): void {
    const audio = audioRef.value
    if (audio) {
      duration.value = audio.duration
      callbacks?.onDurationChange?.(audio.duration)

      // Seek to startAt position on first load
      if (pendingStartAt.value > 0 && Number.isFinite(audio.duration)) {
        const target = clamp(pendingStartAt.value, 0, audio.duration)
        playhead.place(target)
        currentTime.value = target
        pendingStartAt.value = 0
      }
    }
  }

  function consumeAutoPlay(): void {
    if (pendingAutoPlay.value) {
      pendingAutoPlay.value = false
      play()
    }
  }

  function handleCanPlay(): void {
    callbacks?.onCanPlay?.()
    if (playState.value === 'loading' || playState.value === 'buffering') {
      playState.value = 'paused'
    }
    consumeAutoPlay()
  }

  function handlePlay(): void {
    callbacks?.onPlay?.()
  }

  function handlePause(): void {
    playState.value = 'paused'
    callbacks?.onPause?.()
  }

  function handlePlaying(): void {
    playState.value = 'playing'
  }

  function handleWaiting(): void {
    playState.value = 'buffering'
    callbacks?.onWaiting?.()
  }

  function handleTimeUpdate(): void {
    const audio = audioRef.value
    if (audio) {
      currentTime.value = audio.currentTime
      playhead.onTimeUpdate()
      callbacks?.onTimeUpdate?.(audio.currentTime)
    }
  }

  function handleDurationChange(): void {
    const audio = audioRef.value
    if (audio) {
      duration.value = audio.duration
      callbacks?.onDurationChange?.(audio.duration)
    }
  }

  function handleProgress(): void {
    const audio = audioRef.value
    if (audio) {
      buffered.value = calculateBufferedRanges(audio)
      callbacks?.onProgress?.(buffered.value)
    }
  }

  /**
   * Re-read `audio.buffered` after a seek.
   *
   * **`progress` fires while media data is *downloaded*, so it only ever reports the buffer
   * growing.** Nothing is emitted when a range goes away — a browser evicts under memory
   * pressure, and a seek out of the buffered window drops what was held — so without this the
   * bar goes on painting a range that no longer exists until the next append refreshes it.
   */
  function handleSeeked(): void {
    playhead.onSeeked()
    const audio = audioRef.value
    if (audio) {
      buffered.value = calculateBufferedRanges(audio)
    }
  }

  function handleEnded(): void {
    // Continuous playback: when a track ends and auto-advance is enabled, the
    // next track should keep playing rather than just loading paused. The
    // native `ended`/`pause` events have already reset `isPlaying` to false, so
    // we flag the intent here (before navigation changes the source) for the
    // source-URL watcher to pick up.
    if (
      navigation.isAutoAdvance.value &&
      navigation.totalItems.value > 1 &&
      navigation.canGoNext.value
    ) {
      pendingAutoPlay.value = true
    }
    const shouldRestart = navigation.handleTrackEnd()
    if (shouldRestart && audioRef.value) {
      playhead.place(0)
      audioRef.value.play().catch(() => {})
    }
  }

  function handleError(): void {
    const audio = audioRef.value
    if (!audio) return

    const mediaError = audio.error
    let errorCode: PlayerError['code'] = 'PLAYBACK_ERROR'
    let errorMessage = 'Audio playback error'

    if (mediaError) {
      switch (mediaError.code) {
        case MediaError.MEDIA_ERR_ABORTED:
          errorMessage = 'Playback aborted'
          break
        case MediaError.MEDIA_ERR_NETWORK:
          errorCode = 'NETWORK_ERROR'
          errorMessage = 'Network error while loading audio'
          break
        case MediaError.MEDIA_ERR_DECODE:
          errorCode = 'DECODE_ERROR'
          errorMessage = 'Audio decoding error'
          break
        case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
          errorCode = 'FORMAT_NOT_SUPPORTED'
          errorMessage = 'Audio format not supported'
          break
      }
    }

    error.value = {
      code: errorCode,
      message: errorMessage,
      recoverable: true,
    }

    playState.value = 'error'
    callbacks?.onError?.(error.value)
  }

  function handleVolumeChange(): void {
    const audio = audioRef.value
    if (audio) {
      volume.value = audio.volume
      isMuted.value = audio.muted
      callbacks?.onVolumeChange?.(audio.volume, audio.muted)
    }
  }

  function handleRateChange(): void {
    const audio = audioRef.value
    if (audio) {
      playbackSpeed.value = audio.playbackRate
      callbacks?.onRateChange?.(audio.playbackRate)
      callbacks?.onPlaybackSpeedChange?.(audio.playbackRate)
    }
  }

  /**
   * Loads playback info for the current item
   */
  async function loadCurrentPlaybackInfo(): Promise<void> {
    const asset = currentAsset.value
    if (!asset) return

    // If already loaded, we're ready
    if (playbackInfoCache.value.has(asset.uid)) {
      return
    }

    // Request playback info via callback
    playState.value = 'loading'
    if (callbacks?.onPlaybackInfoNeeded) {
      try {
        const info = await callbacks.onPlaybackInfoNeeded(asset.uid as any)
        if (info && info.type === 'audio') {
          setPlaybackInfo(asset.uid, info as AudioPlaybackInfo)
        }
      } catch (err) {
        setError({
          code: 'PLAYBACK_INFO_FETCH_FAILED',
          message: err instanceof Error ? err.message : 'Failed to load audio info',
          recoverable: true,
        })
      }
    }
  }

  /**
   * Loads waveform for the current item
   */
  async function loadCurrentWaveform(): Promise<void> {
    const info = currentPlaybackInfo.value
    if (!info?.waveformUrl?.url) {
      waveformComposable.clear()
      return
    }

    if (config.showWaveform !== false) {
      await waveformComposable.loadWaveform(info.waveformUrl.url)
    }
  }

  // Playback info management
  function getPlaybackInfo(uid: string): AudioPlaybackInfo | undefined {
    return playbackInfoCache.value.get(uid)
  }

  function setPlaybackInfo(uid: string, info: AudioPlaybackInfo): void {
    playbackInfoCache.value.set(uid, info)

    // If this is the current item, update source and load waveform
    if (currentAsset.value?.uid === uid) {
      if (playState.value === 'loading') {
        playState.value = 'idle'
      }
      loadCurrentWaveform()
    }
  }

  function requestPlaybackInfo(uid: string): void {
    if (!playbackInfoCache.value.has(uid) && callbacks?.onPlaybackInfoNeeded) {
      callbacks.onPlaybackInfoNeeded(uid as any).then((info) => {
        if (info && info.type === 'audio') {
          setPlaybackInfo(uid, info as AudioPlaybackInfo)
        }
      }).catch(() => {
        // Handle error silently for prefetch
      })
    }
  }

  // Playback controls
  async function play(): Promise<void> {
    const audio = audioRef.value
    if (!audio) return

    // Make sure we have a source
    if (!currentSourceUrl.value) {
      await loadCurrentPlaybackInfo()
    }

    // The element starts with `preload="metadata"` when autoplay is off, so
    // the actual media isn't downloaded yet. Force a full load and defer
    // the play() to the `canplay` event via pendingAutoPlay — calling
    // play() now would race ahead of the buffer and the browser may reject
    // it as if no source were attached.
    if (audio.readyState < HTMLMediaElement.HAVE_FUTURE_DATA) {
      pendingAutoPlay.value = true
      audio.load()
      return
    }

    try {
      await audio.play()
    } catch (err) {
      if (err instanceof Error) {
        if (err.name === 'NotAllowedError') {
          // Browser blocked autoplay — mute and retry, unmute on first interaction
          const savedVolume = audio.volume
          audio.muted = true
          isMuted.value = true
          try {
            await audio.play()
          } catch {
            // Still blocked, give up silently
            return
          }
          const unmute = () => {
            audio.muted = false
            audio.volume = savedVolume
            isMuted.value = false
            volume.value = savedVolume
            document.removeEventListener('click', unmute)
            document.removeEventListener('keydown', unmute)
            document.removeEventListener('touchstart', unmute)
          }
          document.addEventListener('click', unmute, { once: true })
          document.addEventListener('keydown', unmute, { once: true })
          document.addEventListener('touchstart', unmute, { once: true })
        } else if (err.name !== 'AbortError') {
          setError({
            code: 'PLAYBACK_ERROR',
            message: err.message,
            recoverable: true,
          })
        }
      }
    }
  }

  function pause(): void {
    const audio = audioRef.value
    if (audio) {
      audio.pause()
    }
  }

  async function togglePlay(): Promise<void> {
    if (isPlaying.value) {
      pause()
    } else {
      await play()
    }
  }

  function seek(time: number): void {
    if (!playhead.canSeek.value) return
    seekTo(time)
  }

  function seekTo(time: number): void {
    const audio = audioRef.value
    if (audio && Number.isFinite(audio.duration)) {
      const targetTime = clamp(time, 0, audio.duration)
      const fromTime = audio.currentTime
      callbacks?.onSeeking?.(targetTime)
      playhead.place(targetTime)
      callbacks?.onSeeked?.(fromTime, targetTime)
    }
  }

  function seekPercent(percent: number): void {
    const clampedPercent = clamp(percent, 0, 100)
    const targetTime = (clampedPercent / 100) * duration.value
    seek(targetTime)
  }

  function setVolume(newVolume: number): void {
    const audio = audioRef.value
    if (audio) {
      audio.volume = clamp(newVolume, 0, 1)
    }
  }

  function setMuted(muted: boolean): void {
    const audio = audioRef.value
    if (audio) {
      audio.muted = muted
    }
  }

  function toggleMute(): void {
    setMuted(!isMuted.value)
  }

  function setPlaybackSpeed(speed: number): void {
    const audio = audioRef.value
    if (audio) {
      audio.playbackRate = speed
    }
  }

  // Fullscreen
  function toggleFullscreen(): void {
    isFullscreen.value = !isFullscreen.value
    callbacks?.onFullscreenChange?.(isFullscreen.value)
  }

  // Error management
  function clearError(): void {
    error.value = undefined
    if (playState.value === 'error') {
      playState.value = 'idle'
    }
  }

  function setError(err: PlayerError): void {
    error.value = err
    playState.value = 'error'
    callbacks?.onError?.(err)
  }

  // Lifecycle
  function destroy(): void {
    const audio = audioRef.value
    if (audio) {
      audio.pause()
      audio.src = ''
      unbindAudioEvents(audio)
    }
    playbackInfoCache.value.clear()
    waveformComposable.clear()
  }

  // Watch for audio element changes
  watch(audioRef, (newAudio, oldAudio) => {
    if (oldAudio) {
      unbindAudioEvents(oldAudio)
    }
    if (newAudio) {
      bindAudioEvents(newAudio)
      // Apply initial settings
      newAudio.volume = volume.value
      newAudio.muted = isMuted.value
      newAudio.playbackRate = playbackSpeed.value

      // Load source if already available (initial playback info was set before audio element mounted)
      const url = currentSourceUrl.value
      if (url) {
        newAudio.src = url
        newAudio.load()
      }
    }
  })

  // Watch for source URL changes
  watch(currentSourceUrl, (newUrl) => {
    const audio = audioRef.value
    if (audio && newUrl) {
      const wasPlaying = isPlaying.value

      audio.src = newUrl
      audio.load()
      // `pendingAutoPlay` covers user-triggered Next/Prev/goToIndex that
      // happen while paused — the user expects the new track to play.
      if (config.autoPlay || wasPlaying || pendingAutoPlay.value) {
        scheduleAutoPlay()
      }
    }
  })

  // Watch for items changes
  watch(
    () => config.items,
    (newItems) => {
      if (newItems) {
        items.value = newItems
      }
    },
    { deep: true },
  )

  // Initial load
  if (items.value.length > 0) {
    loadCurrentPlaybackInfo()
    loadCurrentWaveform()
  }

  // Cleanup on unmount
  onUnmounted(() => {
    destroy()
  })

  return {
    // Audio element ref
    audioRef,

    // Items
    items,
    totalItems,

    // Current item
    currentIndex: navigation.currentIndex,
    currentItem,
    currentAsset,
    currentSourceUrl,

    // Playback state
    playState,
    isPlaying,
    isPaused,
    isBuffering,
    isLoading,

    // Time
    currentTime,
    duration,
    buffered,
    progress,

    // Volume
    volume,
    isMuted,

    // Playback speed
    playbackSpeed,
    availableSpeeds: PLAYBACK_SPEEDS,

    // Navigation
    canGoNext: navigation.canGoNext,
    canGoPrev: navigation.canGoPrev,
    isLoop: navigation.isLoop,
    isShuffle: navigation.isShuffle,
    isAutoPlayNext: navigation.isAutoAdvance,
    // User-triggered navigation — explicit intent to play the new track.
    // Set `pendingAutoPlay` BEFORE the index change so the source-URL
    // watcher picks it up alongside `wasPlaying`/`config.autoPlay`.
    goToIndex: (index: number) => {
      pendingAutoPlay.value = true
      navigation.goToIndex(index)
    },
    next: () => {
      pendingAutoPlay.value = true
      navigation.next()
    },
    prev: () => {
      pendingAutoPlay.value = true
      navigation.prev()
    },
    first: () => {
      pendingAutoPlay.value = true
      navigation.first()
    },
    last: () => {
      pendingAutoPlay.value = true
      navigation.last()
    },
    toggleLoop: navigation.toggleLoop,
    toggleShuffle: navigation.toggleShuffle,
    toggleAutoPlayNext: navigation.toggleAutoAdvance,

    // Waveform
    waveform: waveformComposable.waveformData,
    isWaveformLoading: waveformComposable.isLoading,

    // Fullscreen
    isFullscreen,
    toggleFullscreen,

    // Playback controls
    play,
    pause,
    togglePlay,
    canSeek: playhead.canSeek,
    seek,
    seekPercent,
    seekTo,
    setVolume,
    setMuted,
    toggleMute,
    setPlaybackSpeed,

    // Playback info
    currentPlaybackInfo,
    getPlaybackInfo,
    setPlaybackInfo,
    requestPlaybackInfo,

    // Downloads
    hasDownloads,
    sourceDownloadUrl,

    // Error
    error,
    clearError,

    // State
    state,

    // Lifecycle
    destroy,
  }
}
